import { EditableText, defineBlock } from "eddev/blocks"
import { cn, tv, VariantProps } from "@utils/tw"
import { Col, Container, Grid } from "@components/layout/Grid"
import { ComponentPropsWithoutRef, ReactNode, forwardRef, useEffect, useMemo, useRef, useState } from "react"
import { Heading } from "@components/atoms/Heading"
import { Typography } from "@components/atoms/Typography"
import { VSpace } from "@components/layout/Spacer"
import { useIdpDefaults, useIdpCarnet, useCpdApplicationForm } from "@hooks/queries"
import { Link, useHydrating } from "eddev/routing"
import { Button } from "@components/atoms/Button"
import { Chip } from "@components/atoms/Chip"
import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { useIsMobile } from "@hooks/useViewportDerived"
import { Accordion } from "@components/molecules/Accordion"

export const meta: BlockMeta = {
  title: "IDP Carnet check",
  category: "common",
  icon: "id",
  keywords: ["idp"],
  tags: ["root"],
  editMode: "preview",
  lazyLoad: false,
  conditions: {
    postTypes: ["page", "post"],
    multiple: false,
  },
}

export default defineBlock("general/idp-check", (props) => {
  const idpData = useIdpCarnet(undefined)
  const defaultText = useIdpDefaults(undefined)
  const [inputValue, setInputValue] = useState<string>("")
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false)
  const [selection, setSelection] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const isMobile = useIsMobile()
  const hydrating = useHydrating()
  const [smartTraveller, setSmartTraveller] = useState<{
    title: ReactNode
    content: ReactNode
  } | null>(null)
  const cdpApplicationForm = useCpdApplicationForm(undefined)

  const getSmarttraveller = () => {
    if (selection) {
      const country = selection.replace(/\(.*?\)/g, "").trim()
      fetch("/wp-json/smarttraveller/fetch", {
        method: "POST",
        headers: {
          "content-type": "application/x-www-form-urlencoded",
        },
        body: `country=${country}`,
      })
        .then((response) => response.json())
        .then((data: any) => {
          const resp = JSON.parse(data.data)
          if (data.success) {
            const parse = JSON.parse(data.data)

            const description = parse.description.toLowerCase()
            const doNotTravelIndex = description.indexOf("do not travel")
            const reconsiderIndex = description.indexOf("reconsider")
            const highDegreeOfCautionIndex = description.indexOf("high degree of caution")

            const maxIndex = description.length

            const firstIndex = Math.min(
              doNotTravelIndex === -1 ? maxIndex : doNotTravelIndex,
              reconsiderIndex === -1 ? maxIndex : reconsiderIndex,
              highDegreeOfCautionIndex === -1 ? maxIndex : highDegreeOfCautionIndex,
            )

            setSmartTraveller({
              title: (
                <>
                  <Typography as="span" variant="h5">
                    Smartraveller advice
                  </Typography>
                  <Chip
                    color={
                      firstIndex === doNotTravelIndex
                        ? "red"
                        : firstIndex === reconsiderIndex
                          ? "amber"
                          : firstIndex === highDegreeOfCautionIndex
                            ? "altYellow"
                            : "green"
                    }
                    icon="INFO"
                    iconStyle="left"
                  >
                    {!isMobile && (
                      <>
                        {firstIndex === doNotTravelIndex
                          ? "Do not travel"
                          : firstIndex === reconsiderIndex
                            ? "Reconsider your need to travel"
                            : firstIndex === highDegreeOfCautionIndex
                              ? "Exercise a high degree of caution"
                              : "Exercise normal safety precautions"}
                      </>
                    )}
                  </Chip>
                </>
              ),
              content: (
                <>
                  {isMobile && (
                    <Chip
                      color={
                        firstIndex === doNotTravelIndex
                          ? "red"
                          : firstIndex === reconsiderIndex
                            ? "amber"
                            : firstIndex === highDegreeOfCautionIndex
                              ? "altYellow"
                              : "green"
                      }
                      icon="INFO"
                      iconStyle="left"
                    >
                      <>
                        {firstIndex === doNotTravelIndex
                          ? "Do not travel"
                          : firstIndex === reconsiderIndex
                            ? "Reconsider your need to travel"
                            : firstIndex === highDegreeOfCautionIndex
                              ? "Exercise a high degree of caution"
                              : "Exercise normal safety precautions"}
                      </>
                    </Chip>
                  )}
                  <Typography as="div" variant="text-m" dangerouslySetInnerHTML={{ __html: parse.description ?? "" }} />
                  <Button
                    size="tiny"
                    color="grey"
                    href={parse.link}
                    target="_blank"
                    icon="ARROW_UP_RIGHT"
                    iconStyle="right"
                  >
                    Learn More
                  </Button>
                </>
              ),
            })
          } else {
            setSmartTraveller(null)
          }
        })
    }
  }

  const selectedInfo = useMemo(() => {
    if (idpData.data?.idpCarnet && selection) {
      getSmarttraveller()
      return idpData.data.idpCarnet.filter((data) => data?.title === selection)[0] || null
    }
    return null
  }, [selection, isMobile])

  const filteredOptions = () => {
    if (!inputValue) return []
    return (
      idpData.data?.idpCarnet?.filter((data) => data?.title?.toLowerCase().includes(inputValue.toLowerCase())) || []
    )
  }

  useEffect(() => {
    if (inputValue) setSelection(inputRef.current?.value || null)
  }, [inputValue])

  return (
    <Container className="relative z-[5]">
      <Grid>
        <Col initial={12}>
          <IDPCheckContainer black={props.focus ? false : true}>
            <Main>
              <div className="flex flex-col gap-5">
                <EditableText
                  store="title"
                  as={isMobile ? "h2" : "h3"}
                  className="!text-center md:!text-left"
                  placeholder="Enter title..."
                />
                <EditableText store="description" as="p" placeholder="Enter description..." />
              </div>

              <Checker black={props.focus ? false : true}>
                <label className="type-label-caps text-mid-grey pb-2">Which country do you wish to drive in?</label>

                {!hydrating && idpData.data?.idpCarnet && (
                  <div className="flex gap-2">
                    <div className="grow relative">
                      <input
                        ref={inputRef}
                        type="text"
                        className="w-full type-label-l px-3 py-4 rounded-[6px] border-none ring-1 ring-inset ring-mid-grey focus:outline-none focus:ring-2 focus:ring-black"
                        value={inputValue}
                        onChange={(e) => {
                          setDropdownOpen(true)
                          setInputValue(e.target.value)
                        }}
                        placeholder="Search for a country..."
                      />
                      {filteredOptions().length > 0 && (
                        <>
                          {dropdownOpen && (
                            <ul className="absolute text-black top-full z-[1] bg-white mt-2 rounded-[6px] p-2 ring-1 ring-inset ring-mid-grey w-full flex flex-col type-label-m">
                              {filteredOptions()
                                .sort((a, b) => {
                                  const input = inputValue.toLowerCase()
                                  const aTitle = a?.title?.toLowerCase() || ""
                                  const bTitle = b?.title?.toLowerCase() || ""

                                  const aStartsWith = aTitle.startsWith(input)
                                  const bStartsWith = bTitle.startsWith(input)

                                  if (aStartsWith && !bStartsWith) return -1
                                  if (!aStartsWith && bStartsWith) return 1

                                  const aIncludes = aTitle.includes(input)
                                  const bIncludes = bTitle.includes(input)

                                  if (aIncludes && !bIncludes) return -1
                                  if (!aIncludes && bIncludes) return 1

                                  return aTitle.localeCompare(bTitle) // fallback to alphabetical
                                })
                                .map((data, i) => (
                                  <li
                                    key={i}
                                    tabIndex={0}
                                    className="p-1 cursor-pointer list-none hover:bg-yellow focus:bg-yellow"
                                    onClick={() => {
                                      setDropdownOpen(false)
                                      setInputValue(data?.title ?? "")
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        setDropdownOpen(false)
                                        setInputValue(data?.title ?? "")
                                      }
                                    }}
                                  >
                                    {data?.title}
                                  </li>
                                ))}
                            </ul>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </Checker>
            </Main>
            {props?.image && !isMobile && (
              <Image>
                <ResponsiveImage {...props.image} aspect={600 / 368} />
              </Image>
            )}
          </IDPCheckContainer>
          {selectedInfo && (
            <Result>
              <ResultTitle>
                <Typography as="h3">{selectedInfo.title}</Typography>
                <Button
                  size="small"
                  color="white"
                  icon="CLOSE"
                  onClick={() => {
                    setInputValue("")
                    setSelection(null)
                  }}
                />
              </ResultTitle>

              <ResultContent>
                {props.focus ? (
                  <>
                    <Left>
                      <ul>
                        <li className="button-row">
                          <Typography as="label" variant="h5">
                            IDP required?
                          </Typography>
                          <div>
                            <Typography as="span" variant="text-m">
                              {selectedInfo.idpRequired?.toLowerCase() === "yes"
                                ? "Required"
                                : selectedInfo.idpRequired?.toLowerCase() === "no"
                                  ? "Not required"
                                  : selectedInfo.idpRequired?.toLowerCase() === "recommended"
                                    ? "Recommended"
                                    : "Not accepted (please read alert)"}
                            </Typography>

                            {(selectedInfo.idpRequired?.toLowerCase() === "yes" ||
                              selectedInfo.idpRequired?.toLowerCase() === "recommended") && (
                              <ButtonGroup>
                                <Button
                                  size="tiny"
                                  color="grey"
                                  href="/international-motoring/international-driving-permits/"
                                >
                                  Learn more
                                </Button>
                                <Button
                                  size="tiny"
                                  color="yellow"
                                  icon="ARROW_UP_RIGHT"
                                  href="https://www.internationaldrivingpermitsonline.com.au/"
                                  target="_blank"
                                  iconStyle="right"
                                >
                                  Apply online
                                </Button>
                              </ButtonGroup>
                            )}
                          </div>
                        </li>

                        {selectedInfo?.cpdRequired && (
                          <li className="button-row">
                            <Typography as="label" variant="h5">
                              CPD Carnet required?
                            </Typography>

                            <div>
                              {selectedInfo.cpdRequired?.toLowerCase() === "yes" && (
                                <>
                                  <Typography as="span" variant="text-m">
                                    Required
                                  </Typography>

                                  <ButtonGroup>
                                    <Button
                                      size="tiny"
                                      color="grey"
                                      href="/international-motoring/taking-my-vehicle-overseas/"
                                    >
                                      Learn more
                                    </Button>
                                    {cdpApplicationForm.data?.siteSettings?.themeSiteSettings?.idpApplicationPdf
                                      ?.mediaItemUrl && (
                                      <Button
                                        size="tiny"
                                        color="yellow"
                                        icon="DOWNLOAD"
                                        iconStyle="right"
                                        href={
                                          cdpApplicationForm.data?.siteSettings?.themeSiteSettings?.idpApplicationPdf
                                            ?.mediaItemUrl
                                        }
                                        target="_blank"
                                      >
                                        Apply now
                                      </Button>
                                    )}
                                  </ButtonGroup>
                                </>
                              )}

                              {selectedInfo.cpdRequired?.toLowerCase() === "no" && (
                                <Typography as="span" variant="text-m">
                                  Not required
                                </Typography>
                              )}

                              {selectedInfo.cpdRequired?.toLowerCase() === "not set" && (
                                <Typography as="span" variant="text-m">
                                  Requirements currently unavailable
                                </Typography>
                              )}
                            </div>
                          </li>
                        )}

                        {selectedInfo?.drivingSide && (
                          <li>
                            <Typography as="label" variant="h5">
                              Driving side
                            </Typography>

                            <Typography as="span" variant="text-m">
                              {selectedInfo.drivingSide}
                            </Typography>
                          </li>
                        )}
                      </ul>

                      {smartTraveller && (
                        <Accordion title={smartTraveller?.title} content={smartTraveller.content} emergency={null} />
                      )}
                    </Left>
                    <Right>
                      {selectedInfo?.moreDetails && (
                        <Box>
                          <Chip color="warning" icon="INFO" iconStyle="left">
                            Alert
                          </Chip>
                          <VSpace initial={2} />
                          <Typography
                            as="span"
                            variant="text-s"
                            dangerouslySetInnerHTML={{ __html: selectedInfo.moreDetails ?? "" }}
                          />
                        </Box>
                      )}

                      {(selectedInfo.contactDetails ||
                        selectedInfo.telephone ||
                        selectedInfo.website ||
                        selectedInfo.email) && (
                        <Club>
                          <Typography as="span" variant="caps">
                            Local Automotive Club
                          </Typography>
                          <VSpace initial={2} />
                          {selectedInfo?.club && <Heading as="h5">{selectedInfo.club}</Heading>}

                          <div>
                            {selectedInfo?.contactDetails && (
                              <>
                                <VSpace initial={3} />
                                <Typography
                                  as="span"
                                  variant="text-s"
                                  dangerouslySetInnerHTML={{ __html: selectedInfo.contactDetails }}
                                />

                                <VSpace initial={2} />
                              </>
                            )}

                            {selectedInfo?.telephone && (
                              <Typography as="span" variant="text-s">
                                T: {selectedInfo.telephone}
                              </Typography>
                            )}
                            {selectedInfo?.telephone2 && (
                              <Typography as="span" variant="text-s">
                                T: {selectedInfo.telephone2}
                              </Typography>
                            )}
                            {selectedInfo?.telephone3 && (
                              <Typography as="span" variant="text-s">
                                T: {selectedInfo.telephone3}
                              </Typography>
                            )}

                            {selectedInfo?.email && (
                              <Typography as="span" variant="text-s">
                                E: <Link href={`mailto:${selectedInfo.email}`}>{selectedInfo.email}</Link>
                              </Typography>
                            )}
                            {selectedInfo?.website && (
                              <Typography as="span" variant="text-s">
                                W:{" "}
                                <Link href={`${selectedInfo.website}`} target="_blank">
                                  {selectedInfo.website}
                                </Link>
                              </Typography>
                            )}
                          </div>
                        </Club>
                      )}

                      <Box>
                        <Typography as="span" variant="caps">
                          Disclaimer
                        </Typography>
                        <VSpace initial={2} />
                        <ul>
                          {defaultText.data?.siteSettings?.themeSiteSettings?.disclaimer?.map((point, i) => (
                            <li key={i}>{point?.bulletPoint}</li>
                          ))}
                        </ul>
                      </Box>
                    </Right>
                  </>
                ) : (
                  <>
                    <Left>
                      <ul>
                        {selectedInfo?.cpdRequired && (
                          <li className="button-row">
                            <Typography as="label" variant="h5">
                              CPD Carnet required?
                            </Typography>

                            <div>
                              {selectedInfo.cpdRequired?.toLowerCase() === "yes" && (
                                <>
                                  <Typography as="span" variant="text-m">
                                    Required
                                  </Typography>

                                  <ButtonGroup>
                                    {cdpApplicationForm.data?.siteSettings?.themeSiteSettings?.idpApplicationPdf
                                      ?.mediaItemUrl && (
                                      <Button
                                        size="tiny"
                                        color="yellow"
                                        icon="DOWNLOAD"
                                        iconStyle="right"
                                        href={
                                          cdpApplicationForm.data?.siteSettings?.themeSiteSettings?.idpApplicationPdf
                                            ?.mediaItemUrl
                                        }
                                        target="_blank"
                                      >
                                        Apply now
                                      </Button>
                                    )}
                                  </ButtonGroup>
                                </>
                              )}

                              {selectedInfo.cpdRequired?.toLowerCase() === "no" && (
                                <Typography as="span" variant="text-m">
                                  Not required
                                </Typography>
                              )}

                              {selectedInfo.cpdRequired?.toLowerCase() === "not set" && (
                                <Typography as="span" variant="text-m">
                                  Requirements currently unavailable
                                </Typography>
                              )}
                            </div>
                          </li>
                        )}

                        <li className="button-row">
                          <Typography as="label" variant="h5">
                            IDP required?
                          </Typography>
                          <div>
                            <Typography as="span" variant="text-m">
                              {selectedInfo.idpRequired?.toLowerCase() === "yes"
                                ? "Required"
                                : selectedInfo.idpRequired?.toLowerCase() === "no"
                                  ? "Not required"
                                  : selectedInfo.idpRequired?.toLowerCase() === "recommended"
                                    ? "Recommended"
                                    : "Not accepted (please read alert)"}
                            </Typography>

                            {(selectedInfo.idpRequired?.toLowerCase() === "yes" ||
                              selectedInfo.idpRequired?.toLowerCase() === "recommended") && (
                              <ButtonGroup>
                                <Button
                                  size="tiny"
                                  color="grey"
                                  href="/international-motoring/international-driving-permits/"
                                >
                                  Learn more
                                </Button>
                                <Button
                                  size="tiny"
                                  color="yellow"
                                  icon="ARROW_UP_RIGHT"
                                  href="https://www.internationaldrivingpermitsonline.com.au/"
                                  target="_blank"
                                  iconStyle="right"
                                >
                                  Apply online
                                </Button>
                              </ButtonGroup>
                            )}
                          </div>
                        </li>

                        {selectedInfo?.drivingSide && (
                          <li>
                            <Typography as="label" variant="h5">
                              Driving side
                            </Typography>

                            <Typography as="span" variant="text-m">
                              {selectedInfo.drivingSide}
                            </Typography>
                          </li>
                        )}

                        {selectedInfo?.distinguishingPlates && (
                          <li>
                            <Typography as="label" variant="h5">
                              Distinguishing plates
                            </Typography>

                            <Typography as="span" variant="text-m">
                              {selectedInfo.distinguishingPlates}
                            </Typography>
                          </li>
                        )}
                      </ul>

                      {/* <Accordion
                        title={
                          <>
                            <Typography as="span" variant="h5">
                              I’m travelling in the next 5 days
                            </Typography>
                          </>
                        }
                        content={
                          <>
                            <Typography
                              as="p"
                              dangerouslySetInnerHTML={{
                                __html:
                                  "If you require an IDP urgently, you can also purchase one in person through the state and territory motoring clubs. Please ensure you have a completed application form, valid passport photo and current drivers licence photo card with you.",
                              }}
                              css={{ mb: "0 !important" }}
                            />
                            <IDPBranchDropdown />
                            <VSpace initial={4} />
                          </>
                        }
                        emergency={null}
                      /> */}
                      {smartTraveller && (
                        <Accordion title={smartTraveller?.title} content={smartTraveller.content} emergency={null} />
                      )}
                    </Left>
                    <Right>
                      {(selectedInfo?.moreDetails || selectedInfo?.specialRequirements) && (
                        <Box>
                          <Chip color="warning" icon="INFO" iconStyle="left">
                            Alert
                          </Chip>
                          <VSpace initial={2} />
                          <Typography
                            as="span"
                            variant="text-s"
                            dangerouslySetInnerHTML={{ __html: selectedInfo?.moreDetails ?? "" }}
                          />

                          <Typography
                            as="span"
                            variant="text-s"
                            dangerouslySetInnerHTML={{ __html: selectedInfo?.specialRequirements ?? "" }}
                          />
                        </Box>
                      )}

                      {(selectedInfo.contactDetails ||
                        selectedInfo.telephone ||
                        selectedInfo.website ||
                        selectedInfo.email) && (
                        <Club>
                          <Typography as="span" variant="caps">
                            Local Automotive Club
                          </Typography>
                          <VSpace initial={2} />
                          {selectedInfo?.club && <Heading as="h5">{selectedInfo.club}</Heading>}

                          <div>
                            {selectedInfo?.contactDetails && (
                              <>
                                <VSpace initial={3} />
                                <Typography
                                  as="span"
                                  variant="text-s"
                                  dangerouslySetInnerHTML={{ __html: selectedInfo.contactDetails }}
                                />

                                <VSpace initial={2} />
                              </>
                            )}

                            {selectedInfo?.telephone && (
                              <Typography as="span" variant="text-s">
                                T: {selectedInfo.telephone}
                              </Typography>
                            )}
                            {selectedInfo?.telephone2 && (
                              <Typography as="span" variant="text-s">
                                T: {selectedInfo.telephone2}
                              </Typography>
                            )}
                            {selectedInfo?.telephone3 && (
                              <Typography as="span" variant="text-s">
                                T: {selectedInfo.telephone3}
                              </Typography>
                            )}

                            {selectedInfo?.email && (
                              <Typography as="span" variant="text-s">
                                E: <Link href={`mailto:${selectedInfo.email}`}>{selectedInfo.email}</Link>
                              </Typography>
                            )}
                            {selectedInfo?.website && (
                              <Typography as="span" variant="text-s">
                                W:{" "}
                                <Link href={`${selectedInfo.website}`} target="_blank">
                                  {selectedInfo.website}
                                </Link>
                              </Typography>
                            )}
                          </div>
                        </Club>
                      )}

                      <Box>
                        <Typography as="span" variant="caps">
                          Disclaimer
                        </Typography>
                        <VSpace initial={2} />
                        <ul>
                          {defaultText.data?.siteSettings?.themeSiteSettings?.disclaimer?.map((point, i) => (
                            <li key={i}>{point?.bulletPoint}</li>
                          ))}
                        </ul>
                      </Box>
                    </Right>
                  </>
                )}
              </ResultContent>
            </Result>
          )}
        </Col>
      </Grid>
    </Container>
  )
})

const idpCheckContainerStyles = tv({
  base: [
    "my-5 bg-yellow rounded-md flex flex-row gap-4 text-black z-[1] relative",
    "[&_h2]:text-black [&_h2]:my-0 [&_h2]:type-title-l [&_h2]:text-center [&_h2]:block [&_h2]:w-full",
    "[&_h3]:text-black [&_h3]:my-0 [&_h3]:type-title-m",
    "[&>div>div>p]:my-2 [&>div>div>p]:type-body-m [&>div>div>p]:text-center",
    "md:[&>div>div>p]:my-0 md:[&>div>div>p]:text-left",
  ],
  variants: {
    black: {
      true: "bg-black text-white [&_h2]:text-yellow [&_h3]:text-yellow",
    },
  },
})

const IDPCheckContainer = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<"div"> & VariantProps<typeof idpCheckContainerStyles>
>(({ black, className, ...props }, ref) => (
  <div ref={ref} className={idpCheckContainerStyles({ black, className })} {...props} />
))
IDPCheckContainer.displayName = "IDPCheckContainer"

const Main = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex flex-col gap-6 py-7 px-8 justify-between grow", className)} {...props} />
))
Main.displayName = "Main"

const Image = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "w-1/2 min-w-[50%] block rounded-[0_12px_12px_0] overflow-hidden",
      "[&_.image-wrapper]:pt-0 [&_.image-wrapper]:h-full",
      className,
    )}
    {...props}
  />
))
Image.displayName = "Image"

const checkerStyles = tv({
  base: "flex flex-col [&_label]:text-dark-grey [&_label]:mb-2",
  variants: {
    black: {
      true: "[&_label]:text-light-grey",
    },
  },
})

const Checker = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div"> & VariantProps<typeof checkerStyles>>(
  ({ black, className, ...props }, ref) => <div ref={ref} className={checkerStyles({ black, className })} {...props} />,
)
Checker.displayName = "Checker"

const Result = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("bg-white border border-grey pt-8 pr-7 pb-7 pl-7 rounded-md -mt-8 z-0 relative", className)}
    {...props}
  />
))
Result.displayName = "Result"

const ResultTitle = forwardRef<HTMLSpanElement, ComponentPropsWithoutRef<"span">>(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn(
      "flex items-center mb-4 w-full justify-between [&_h3]:type-title-m [&_h3]:grow [&_h3]:my-0 [&_button]:ml-3",
      className,
    )}
    {...props}
  />
))
ResultTitle.displayName = "ResultTitle"

const ResultContent = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex gap-5 flex-col md:flex-row", className)} {...props} />
))
ResultContent.displayName = "ResultContent"

const Left = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "w-full",
      "[&_ul]:w-full [&_ul]:mt-3 [&_ul]:px-0",
      "[&_ul_li]:flex [&_ul_li]:border-t [&_ul_li]:border-light-grey [&_ul_li]:py-3 [&_ul_li]:px-0 [&_ul_li]:gap-4",
      "[&_ul_li_label]:flex [&_ul_li_label]:w-[200px] [&_ul_li_label]:min-w-[200px] [&_ul_li_label]:items-center",
      "[&_ul_li_span]:flex",
      "[&_ul_li>div]:w-full [&_ul_li>div]:flex [&_ul_li>div]:items-center [&_ul_li>div]:justify-between",
      "[&_ul_li.button-row]:flex-col",
      "md:w-[calc((100%-var(--spacing-5))*(2/3))] md:[&_ul_li.button-row]:flex-row",
      className,
    )}
    {...props}
  />
))
Left.displayName = "Left"

const Right = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("w-full flex flex-col gap-5 md:w-[calc((100%-var(--spacing-5))*(1/3))]", className)}
    {...props}
  />
))
Right.displayName = "Right"

const Club = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex flex-col rounded-[6px] bg-light-grey p-4 w-full box-border",
      "[&>div]:flex [&>div]:flex-col [&>div_a]:underline",
      className,
    )}
    {...props}
  />
))
Club.displayName = "Club"

const ButtonGroup = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex gap-4", className)} {...props} />
))
ButtonGroup.displayName = "ButtonGroup"

const Box = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "bg-light-grey flex flex-col items-start rounded-[6px] p-4 w-full box-border",
      "[&>ul_li]:my-3 [&>ul_li]:py-0 [&>ul_li]:type-body-s [&>ul_li]:list-none [&>ul_li]:pl-6 [&>ul_li]:relative",
      "[&>ul_li]:before:content-[''] [&>ul_li]:before:absolute [&>ul_li]:before:top-[8px] [&>ul_li]:before:left-[8px] [&>ul_li]:before:w-[8px] [&>ul_li]:before:h-[8px] [&>ul_li]:before:rounded-[8px] [&>ul_li]:before:bg-yellow",
      className,
    )}
    {...props}
  />
))
Box.displayName = "Box"
